"use client";

import { css } from "styled-components";

import { cssFromVariantTheme } from "../../slate/Button/styles";

import { StyledIconWrapper } from "./IconButton";
import { iconWrapperTheme } from "./theme";
import {
  IconButtonSizes,
  IconButtonShapes,
  StyledIconWrapperProps,
  StyledIconButtonProps,
} from "./types";

const M_BUTTON_HEIGHT = 48;
const S_BUTTON_HEIGHT = 40;

const calculateButtonSize = (size: IconButtonSizes) => {
  return `${size === "small" || size === "S" ? S_BUTTON_HEIGHT : M_BUTTON_HEIGHT}px`;
};

const CIRCLE_BORDER_RADIUS = "50%";
const SQUARE_BORDER_RADIUS = "var(--slate-default-component-radius)";

const calculateBorderRadius = (shape: IconButtonShapes) => {
  return `${shape === "circle" ? CIRCLE_BORDER_RADIUS : SQUARE_BORDER_RADIUS}`;
};

export const IconButtonStyles = (buttonProps: StyledIconButtonProps) => {
  const {
    $loading = false,
    $shape = "circle",
    $variant = "primary",
  } = buttonProps;
  const variantTheme = iconWrapperTheme[$variant];
  return css`
    align-items: center;
    block-size: ${M_BUTTON_HEIGHT}px; // Use M size for all to pass tap target sizing
    border-radius: ${calculateBorderRadius($shape)};
    cursor: pointer;
    display: flex;
    inline-size: ${M_BUTTON_HEIGHT}px;
    justify-content: center;

    &:disabled {
      cursor: not-allowed;
    }

    &:focus-visible {
      // Focus styles are removed in a global file
      // This restores the outlines
      outline: 5px auto -webkit-focus-ring-color;
      outline-offset: 2px;

      ${StyledIconWrapper} {
        ${cssFromVariantTheme(variantTheme, "focus", buttonProps)}
      }
    }

    &:hover {
      ${StyledIconWrapper} {
        ${cssFromVariantTheme(variantTheme, "hover", buttonProps)}
      }
    }

    &:active {
      ${StyledIconWrapper} {
        ${cssFromVariantTheme(variantTheme, "active", buttonProps)}
      }
    }

    &:disabled,
    &[disabled] {
      ${StyledIconWrapper} {
        ${$loading
          ? cssFromVariantTheme(variantTheme, "loading", buttonProps)
          : cssFromVariantTheme(variantTheme, "disabled", buttonProps)}
        cursor: not-allowed;
      }
    }
  `;
};

export const IconWrapperStyles = (buttonProps: StyledIconWrapperProps) => {
  const {
    $shape = "circle",
    $size = "medium",
    $variant = "primary",
  } = buttonProps;
  const variantTheme = iconWrapperTheme[$variant];

  return css`
    align-items: center;
    block-size: ${calculateButtonSize($size)};
    border-radius: ${calculateBorderRadius($shape)};
    box-sizing: border-box;
    display: flex;
    fill: currentColor; // allows SVGs to match parent color
    inline-size: ${calculateButtonSize($size)};
    justify-content: center;
    transition:
      background-color 0.2s ease,
      border 0.2s ease;

    ${cssFromVariantTheme(variantTheme, "base", buttonProps)}

    svg {
      block-size: 16px;
      color: inherit;
      inline-size: 16px;
    }
  `;
};
